<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="suppliers"
        :row-key="'id'"
        :loading="loading"
        :pagination="tablePagination"
        :scroll="{ x: 1160 }"
        class="text-nowrap align-middle people-table"
        @change="handleTableChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("supplier_page.no_suppliers_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="Number(record.status || 0) === 1 ? 'status-active' : 'status-inactive'">
              {{ Number(record.status || 0) === 1 ? t("supplier_page.status_active") : t("supplier_page.status_inactive") }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('supplier_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#supplierDetailsModal"
                @click="$emit('view-supplier', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('supplier_page.view')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('supplier_page.edit')"
                data-bs-toggle="modal"
                data-bs-target="#supplierCreateModal"
                @click="$emit('edit-supplier', record)"
              >
                <img src="../../../assets/img/icons/edit.svg" :alt="t('supplier_page.edit')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('supplier_page.delete')"
                @click="$emit('delete-supplier', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('supplier_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Supplier } from "../../../services/supplier";

export default defineComponent({
  name: "SupplierList",
  props: {
    suppliers: {
      type: Array as () => Supplier[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
      },
      required: true,
    },
  },
  emits: ["view-supplier", "edit-supplier", "delete-supplier", "page-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t("supplier_page.date"), key: "date", align: "center" as const, width: 210 },
      { title: t("supplier_page.supplier_code"), dataIndex: "code", key: "code", align: "center" as const, width: 150 },
      { title: t("supplier_page.supplier_name"), dataIndex: "name", key: "name", align: "center" as const, width: 220 },
      { title: t("supplier_page.email"), dataIndex: "email", key: "email", align: "center" as const, width: 220 },
      { title: t("supplier_page.phone"), dataIndex: "phone", key: "phone", align: "center" as const, width: 160 },
      { title: t("supplier_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("supplier_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);

    const tablePagination = computed(() => ({
      current: props.pagination.current_page,
      pageSize: props.pagination.per_page,
      total: props.pagination.total,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      locale: { items_per_page: t("supplier_page.items_per_page") },
    }));

    const handleTableChange = (paginationInfo: { current?: number; pageSize?: number }) => {
      emit("page-change", paginationInfo.current || 1, paginationInfo.pageSize || 10);
    };

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("supplier_page.not_available");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    return {
      t,
      columns,
      tablePagination,
      handleTableChange,
      formatDate,
    };
  },
});
</script>

<style scoped>
.people-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.people-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  color: #1f9254;
  background: #e8f7ee;
}

.status-inactive {
  color: #d33c43;
  background: #fdecec;
}
</style>
